import React, { createContext, useContext } from "react"

/**
 * Page-level props for the current page, shared via context and passed to conversion requests
 * (newsletter signup, CTAs, etc.).
 *
 * - `path` — the current page path.
 * - `resourceSgid` — a **server-issued signed GlobalID** for the page's resource. The server issues
 *   it per page render (it encodes type + id and is tamper-proof) and decodes it back to the real
 *   record on the request. We never send a raw resource id — clients can spoof those.
 *
 * UTMs are deliberately NOT here: they're captured server-side from the Ahoy visit.
 */
/** How the page is chrome-wrapped by the server layout. Absent means a normal, chromed page. */
export type RenderContext = "standard" | "fullscreen"

export interface PageProps {
  path?: string
  resourceSgid?: string
  /**
   * How the server rendered the page shell (mirrors the layout's `fullscreen?`). Lets React adapt to the
   * chrome without a per-page override — e.g. AppShell drops the floating action menu when "fullscreen".
   */
  renderContext?: RenderContext
  /**
   * Server-injected demo data for the arcade embed (only the codex show page sets these) — the real
   * word-guesser game + demo records, so the embed's minigame.* analytics use real gids/slugs. Absent
   * elsewhere; the embed falls back to client fixtures. (A pragmatic coupling — see note below.)
   */
  demoGame?: PageDemoRecord
  demoRecords?: PageDemoRecord[]
}

/** A minimal record/game shape the server serializes into page_props for demo surfaces. */
export interface PageDemoRecord {
  name: string
  translation?: string | null
  pronunciation?: string | null
  slug?: string
  gid?: string
}

const PagePropsContext = createContext<PageProps>({})

export interface PagePropsProviderProps {
  /** Populate once from server-injected page data. */
  value: PageProps
  children: React.ReactNode
}

/**
 * Wrap the app (or a page subtree) with the current page's props:
 *
 *   <PagePropsProvider value={{ path, resourceSgid }}>
 *     …
 *   </PagePropsProvider>
 *
 * Any conversion component below reads them via usePageProps() and includes them in its request.
 */
export const PagePropsProvider: React.FC<PagePropsProviderProps> = ({ value, children }) => (
  <PagePropsContext.Provider value={value}>{children}</PagePropsContext.Provider>
)

/** Read the current page's props to include them in a request (mutation). */
export const usePageProps = (): PageProps => useContext(PagePropsContext)

export default PagePropsProvider
